import { createSlice } from '@reduxjs/toolkit';
import { snippet } from './normalize';

function upsertConversation(state, id, prompt) {
    if (!id) return;
    const title = snippet(prompt) || 'New conversation';
    const updatedAt = new Date().toISOString();
    const existing = state.conversations.find((conversation) => conversation.ai_conversation_id === id);
    if (existing) {
        existing.updated_at = updatedAt;
        if (!existing.title || existing.title === 'New conversation') existing.title = title;
        return;
    }
    state.conversations.unshift({
        ai_conversation_id: id,
        title,
        updated_at: updatedAt
    });
}

export const aiChatSlice = createSlice({
    name: 'aiChat',
    initialState: {
        conversations: [],
        isConversationsLoading: false,
        isConversationsFetched: false,
        activeConversationId: null,
        messages: [],
        isMessagesLoading: false,
        isSending: false,
        failedPrompt: null
    },
    reducers: {
        onCallConversations: (state) => {
            state.isConversationsLoading = !state.isConversationsFetched;
        },
        onSuccessConversations: (state, { payload }) => {
            state.isConversationsLoading = false;
            state.isConversationsFetched = true;
            state.conversations = payload;
        },
        onFailedConversations: (state) => {
            state.isConversationsLoading = false;
            state.isConversationsFetched = true;
        },
        selectConversation: (state, { payload }) => {
            state.activeConversationId = payload;
            state.messages = [];
            state.isMessagesLoading = true;
            state.isSending = false;
            state.failedPrompt = null;
        },
        startNewChat: (state) => {
            state.activeConversationId = null;
            state.messages = [];
            state.isMessagesLoading = false;
            state.isSending = false;
            state.failedPrompt = null;
        },
        onCallMessages: (state, { payload }) => {
            if (state.isSending && state.activeConversationId === payload.conversationId) return;
            state.activeConversationId = payload.conversationId;
            state.messages = [];
            state.isMessagesLoading = true;
            state.failedPrompt = null;
        },
        onSuccessMessages: (state, { payload }) => {
            if (payload.conversationId !== state.activeConversationId) return;
            state.isMessagesLoading = false;
            state.messages = payload.messages;
        },
        onFailedMessages: (state) => {
            state.isMessagesLoading = false;
        },
        onCallSendPrompt: (state, { payload }) => {
            state.isSending = true;
            state.failedPrompt = null;
            const existing = state.messages.find((message) => message.id === payload.localMessageId);
            if (existing) {
                existing.status = 'pending';
                return;
            }
            state.messages.push({
                id: payload.localMessageId,
                role: 'user',
                text: payload.prompt,
                toolSummary: '',
                downloads: [],
                attachments: (payload.attachmentNames || []).map((name) => ({ name })),
                status: 'pending'
            });
        },
        onSuccessSendPrompt: (state, { payload }) => {
            state.isSending = false;
            const userMessage = state.messages.find((message) => message.id === payload.localMessageId);
            if (!userMessage) return;
            userMessage.status = 'sent';
            state.failedPrompt = null;
            if (payload.aiConversationId) {
                state.activeConversationId = payload.aiConversationId;
                upsertConversation(state, payload.aiConversationId, payload.prompt);
            }
            state.messages.push({
                id: `assistant-${payload.localMessageId}`,
                role: 'assistant',
                text: payload.answer,
                toolSummary: payload.toolSummary || '',
                downloads: payload.downloads || [],
                status: 'sent'
            });
        },
        onFailedSendPrompt: (state, { payload }) => {
            state.isSending = false;
            const userMessage = state.messages.find((message) => message.id === payload.localMessageId);
            if (!userMessage) return;
            userMessage.status = 'failed';
            state.failedPrompt = { id: payload.localMessageId, text: payload.prompt };
        }
    }
});

export const {
    onCallConversations,
    onSuccessConversations,
    onFailedConversations,
    selectConversation,
    startNewChat,
    onCallMessages,
    onSuccessMessages,
    onFailedMessages,
    onCallSendPrompt,
    onSuccessSendPrompt,
    onFailedSendPrompt
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
